import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo, LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Compass, HeartHandshake, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { role: "Paciente", email: "joao@compasso.app", desc: "Experiência acolhedora e simples" },
  { role: "Profissional", email: "ana@compasso.app", desc: "Painel clínico com IA contextual" },
  { role: "Admin da Organização", email: "brunolr@gmail.com", desc: "Gestão de profissionais e pacientes" },
];

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ email, password, name, role: "patient" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const quick = async (mail) => {
    setLoading(true);
    try { await login(mail, "Compasso@2025"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-gradient-to-br from-[#0F766E] via-[#0d6259] to-[#1F3B4D] text-white">
        <div className="absolute -right-16 -top-16 w-96 h-96 rounded-full bg-[#34D399]/20 blur-3xl" />
        <div className="absolute -left-10 bottom-10 w-72 h-72 rounded-full bg-[#60A5FA]/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <LogoMark size={44} />
          <span className="font-heading font-extrabold text-2xl">Compasso <span className="text-[#34D399]">Cuidar</span></span>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Cuidado que continua entre as sessões.</h1>
          <p className="mt-4 text-white/80 leading-relaxed">
            Uma plataforma de continuidade do acompanhamento entre paciente e profissional — organizando o que acontece antes, durante e principalmente entre uma sessão e outra.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { i: Compass, t: "Orientação", d: "Um compasso para cada etapa" },
              { i: Users, t: "Conexão", d: "Você não está só nessa jornada" },
              { i: HeartHandshake, t: "Cuidado", d: "Que vai além das sessões" },
              { i: RefreshCw, t: "Continuidade", d: "Suporte no seu dia a dia" },
            ].map((c, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center shrink-0"><c.i size={18} /></div>
                <div><div className="font-semibold text-sm">{c.t}</div><div className="text-xs text-white/70">{c.d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/50">© {new Date().getFullYear()} Compasso Cuidar</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>
          <h2 className="font-heading text-2xl font-bold text-[#1F3B4D]">
            {mode === "login" ? "Entrar" : "Criar conta"}
          </h2>
          <p className="text-sm text-[#94A3B8] mt-1">
            {mode === "login" ? "Acesse seu acompanhamento com segurança." : "Comece sua jornada de cuidado."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Nome</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  required className="rounded-xl h-11 border-[#E0F2FE]" placeholder="Seu nome" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
                required className="rounded-xl h-11 border-[#E0F2FE]" placeholder="voce@email.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                required className="rounded-xl h-11 border-[#E0F2FE]" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 rounded-full bg-[#0F766E] hover:bg-[#0d6259] gap-2">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>{mode === "login" ? "Entrar" : "Criar conta"} <ArrowRight size={16} /></>}
            </Button>
          </form>

          <div className="mt-4 flex items-center gap-3 text-xs text-[#94A3B8]"><div className="flex-1 h-px bg-[#E0F2FE]" /> ou <div className="flex-1 h-px bg-[#E0F2FE]" /></div>
          <Button type="button" variant="outline" onClick={loginWithGoogle} data-testid="google-login-button"
            className="mt-4 w-full h-11 rounded-full border-[#E0F2FE] gap-2 text-[#1F3B4D] hover:bg-[#F8FAFC]">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22 22-9.8 22-22c0-1.5-.2-2.6-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.6 2 8.3 6.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 46c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 36.5 26.9 37.5 24 37.5c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C8.2 41 15.5 46 24 46z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5C41.3 36.9 44 31 44 24c0-1.5-.2-2.6-.4-3.5z"/></svg>
            Continuar com Google
          </Button>

          <button onClick={() => setMode(mode === "login" ? "register" : "login")}
            data-testid="toggle-auth-mode" className="mt-4 text-sm text-[#0F766E] hover:underline">
            {mode === "login" ? "Não tem conta? Criar agora" : "Já tenho conta — entrar"}
          </button>

          <div className="mt-8">
            <div className="flex items-center gap-3 text-xs text-[#94A3B8]"><div className="flex-1 h-px bg-[#E0F2FE]" /> Acesso rápido de demonstração <div className="flex-1 h-px bg-[#E0F2FE]" /></div>
            <div className="mt-4 space-y-2">
              {DEMO.map((d) => (
                <button key={d.email} onClick={() => quick(d.email)} disabled={loading}
                  data-testid={`demo-login-${d.role.split(" ")[0].toLowerCase()}`}
                  className="w-full text-left px-4 py-3 rounded-xl bg-white border border-[#E0F2FE] hover:border-[#34D399] card-hover flex items-center justify-between">
                  <div><div className="text-sm font-semibold text-[#1F3B4D]">{d.role}</div><div className="text-xs text-[#94A3B8]">{d.desc}</div></div>
                  <ArrowRight size={16} className="text-[#0F766E]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
